import { activeFacebookPageSelector } from '@mm/state/selectors/facebookPageSelector';
import React, { useEffect, useState } from 'react';
import { Redirect, Route, withRouter } from 'react-router-dom';
import { compose } from 'recompose';
import { useDispatch, useSelector } from 'react-redux';
import { findIndex } from 'lodash';
import { StoreState } from '@mm/state/store-interfaces';
import connectedPageCRUDActions from '@mm/state/actions/ConnectedPageCRUDActions';
import { OnboardingSignupStep } from '@mm/state/model/CompanyUsersModel';
import chatbotCRUDActions, {
  useChatbotCRUDActions,
} from '@mm/state/actions/ChatbotCRUDActions';
import { activeCompanySelector } from '@mm/state/selectors/viewSelectors';
import { LOGIN, CHATBOT_EDITOR } from '../../../common/constants/routes';
import { ONBOARDING_ROUTES, OnboardingRouteDefinition } from './constants';
import Spinner from '../../../common/components/Spinner';
import { Routes } from '../../../routes/top-level-routes';
import { RouterComponentProps } from '../../../common/helpers/routes/interfaces';
import { useOpenOnboardingErrorModal } from './use-open-onboarding-error-modal';
import reduxStore from '@mm/state';

type OuterProps = {
  component: React.ComponentType<any>;
  path: string;
};

type InnerProps = RouterComponentProps;

type Props = OuterProps & InnerProps;

enum FetchState {
  NEEDS_FETCH,
  FETCHING,
  FETCH_COMPLETE,
}

const getCanEnterRoute = (
  path: string,
  signupStepRoute: OnboardingRouteDefinition,
) => {
  if (path === Routes.signupOnboarding.path) {
    return false;
  }

  const signupStepIndex =
    findIndex(ONBOARDING_ROUTES, { id: signupStepRoute.id }) || 0;
  const currentPathIndex = findIndex(ONBOARDING_ROUTES, { path });

  return signupStepIndex >= currentPathIndex;
};

// Gets users last completed onboarding step or the first onboarding step
const getSignupStepRoute = (signupStep: OnboardingSignupStep) =>
  ONBOARDING_ROUTES.find(({ id }) => id === signupStep) || ONBOARDING_ROUTES[0];

const getRedirectPath = (path: string, signupStep: OnboardingSignupStep) => {
  const signupStepRoute = getSignupStepRoute(signupStep);
  const canEnterRoute = getCanEnterRoute(path, signupStepRoute);

  if (canEnterRoute) {
    return null;
  }

  return signupStepRoute.path;
};

const getOnboardingStepComponent = (path: string) => {
  const route =
    ONBOARDING_ROUTES.find(route => route.path === path) ||
    ONBOARDING_ROUTES[1];

  return route.Component;
};

const enhance = compose<InnerProps, OuterProps>(withRouter);

const usePrimeState = () => {
  const dispatch = useDispatch();
  const [fetchState, setFetchState] = useState(FetchState.NEEDS_FETCH);
  const { openOnboardingErrorModal } = useOpenOnboardingErrorModal();
  const company = useSelector(activeCompanySelector);
  const activePage = useSelector(activeFacebookPageSelector);
  const pages = useSelector((state: StoreState) =>
    state.connectedPage.entities.filter(
      ({ companyId }) => companyId === company?.id,
    ),
  );
  const session = useSelector((state: StoreState) => state.session.session);
  const { retrieveBasicListByCompany } = useChatbotCRUDActions();

  const primeState = async () => {
    setFetchState(FetchState.FETCHING);

    const { userAttributes } = session;

    if (
      userAttributes.onboardingSignupStep ===
        OnboardingSignupStep.CONNECT_PAGE &&
      company
    ) {
      await dispatch(
        connectedPageCRUDActions.retrieveAllByCompany(company.id, 0, true),
      );
    } else if (company) {
      const { onboardingSignupBot } = userAttributes;
      const bots = await retrieveBasicListByCompany(company.id);
      const companyBotId = bots?.[0]?.id;

      const botId = onboardingSignupBot ? onboardingSignupBot : companyBotId;
      if (botId) {
        await Promise.all([
          dispatch(chatbotCRUDActions.retrieve(botId)),
          dispatch(
            connectedPageCRUDActions.retrieveAllByCompany(company.id, 0, true),
          ),
        ]);
      }
    }

    setFetchState(FetchState.FETCH_COMPLETE);
  };

  useEffect(() => {
    if (company && fetchState === FetchState.NEEDS_FETCH) {
      primeState();
    }
  }, [company, primeState, fetchState]);

  return { fetchState, company, pages };
};

const OnboardingParentRoute = enhance((props: Props) => {
  const { fetchState, company, pages } = usePrimeState();

  return (
    <Route
      path={props.path}
      render={props => {
        const session = reduxStore.getState()?.session?.session;
        const path = props.location.pathname;

        if (!session) {
          return <Redirect to={LOGIN} />;
        }

        const { userAttributes } = session;

        if (
          userAttributes &&
          userAttributes.onboardingSignupStep === OnboardingSignupStep.NONE
        ) {
          return <Redirect to={CHATBOT_EDITOR} />;
        }

        if (fetchState === FetchState.FETCH_COMPLETE && userAttributes) {
          const redirectPath = getRedirectPath(
            path,
            userAttributes.onboardingSignupStep,
          );

          if (redirectPath) {
            return <Redirect to={redirectPath} />;
          }

          const activeRouteDefinition = ONBOARDING_ROUTES.find(
            route => route.path === path,
          );

          if (activeRouteDefinition) {
            const Component: any = getOnboardingStepComponent(path);

            return <Component {...props} />;
          }

          // If the route does not exist redirect user to last completed onboarding step
          const signupStepRoute = getSignupStepRoute(
            userAttributes.onboardingSignupStep,
          );

          return <Redirect to={signupStepRoute.path} />;
        }

        return <Spinner show={true} />;
      }}
    />
  );
});

export default OnboardingParentRoute;
